import { useMemo } from "react";
import { useDeleteContract, useGetContractsByMovie } from "@/services/queries/useContratos";
import { ActionConfig, ColumnConfig, FilterConfig } from "@/components/table-builder/types";
import { useLocation } from "wouter";
import { ContratoRow } from "../contrato.types";
import { StatusBadge } from "../components/contrato-list/status-badge";
import { useProjetoContext } from "@/context/projeto/projeto-context";
import { reducer } from "@/hooks/use-toast";

export function useContratoList() {
  const [, navigate] = useLocation();

  const { projetoSelecionado } = useProjetoContext();
  const movieId = projetoSelecionado?.id || 0;

  const { data: contracts, isLoading, error } = useGetContractsByMovie(movieId)
  const { mutateAsync: deleteContract } = useDeleteContract()

  const tableData: ContratoRow[] = useMemo(() => {
    if (!contracts?.data) return [];
    const seen = new Set<number>()
    return contracts.data
      .reduce<ContratoRow[]>((acc, contract) => {
        const id = contract.staffUser?.person?.id ?? 0
        if (seen.has(id)) return acc
        seen.add(id)
        acc.push({
          id: contract.id,
          personName: contract.staffUser?.person?.name || "Sem nome",
          phone: "N/A",
          email: contract.staffUser?.login || "N/A",
          team: contract.staffUser?.person?.role || "Sem função",
          status: contract.contractStatus,
          date: new Date(contract.createdAt).toLocaleDateString('pt-BR'),
        });
        return acc;
      }, []);
  }, [contracts]);

  const contractStats = useMemo(() => {
    if (!contracts?.data || contracts?.data.length === 0) {
      return {
        expiring: 0,
        active: 0,
        completed: 0,
      };
    }
    
    const seenCon = new Set<number>()
    const contratos = contracts?.data
      .reduce((acc: typeof contracts.data, contract) => {
        const id = contract.staffUser?.person?.id ?? 0
        if (seenCon.has(id)) return acc
        seenCon.add(id)
        acc.push(contract)
        return acc
      }, [])

    const now = new Date();
    const thirtyDaysFromNow = new Date();
    thirtyDaysFromNow.setDate(now.getDate() + 30);

    const expiring = contratos.filter(contract => {
      const endDate = new Date(contract.contractTill);
      return endDate > now && endDate <= thirtyDaysFromNow && contract.active;
    }).length;

    const seen = new Set<number>()
    const active = contratos
      .filter(contract =>
        contract.contractStatus === "ACTIVE" && contract.active
      ).length;

    const completed = contratos.filter(contract =>
      contract.contractStatus === "SIGNED"
    ).length;

    return { expiring, active, completed };
  }, [contracts])

  const tableActions: ActionConfig<ContratoRow> = {
    onView: (row) => navigate(`/contratual/contratos/${row.id}`),
    onEdit: (row) => navigate(`/contratual/contratos/${row.id}/editar`),
    onDelete: async (row) => {
      const confirmed = window.confirm(
        `Tem certeza que deseja remover o contrato de ${row.personName}? Esta ação não pode ser desfeita.`
      );
      if (confirmed) {
        await deleteContract(row.id);
      }
    },
  };

  const columns: ColumnConfig<ContratoRow>[] = [
    {
      key: "personName",
      label: "Nome",
      searchable: true,
      render: (_, row) => (
        <div className="flex items-center gap-3">
          <div className="w-[39px] h-[39px] rounded-full bg-gray-600 flex items-center justify-center">
            <span className="text-xs font-medium text-white">
              {row.personName.split(" ").map((n) => n[0]).join("").toUpperCase().slice(0, 2)}
            </span>
          </div>
          <span className="text-white text-sm font-medium">
            {row.personName}
          </span>
        </div>
      ),
    },
    {
      key: "phone",
      label: "Telefone",
      searchable: true,
    },
    {
      key: "email",
      label: "E-mail",
      searchable: true,
    },
    {
      key: "team",
      label: "Equipe/Função",
      searchable: true,
    },
    {
      key: "status",
      label: "Status",
      render: (value) => <StatusBadge status={String(value)} />,
    },
    {
      key: "date",
      label: "Data",
    },
  ];

  const filterConfig: FilterConfig[] = [
    {
      key: "status",
      label: "Status",
      type: "select",
      options: [
        { value: "ACTIVE", label: "Em Execução" },
        { value: "PENDING", label: "Pendente" },
        { value: "SIGNED", label: "Concluído" },
        { value: "EXPIRED", label: "Vencido" },
      ],
    },
    {
      key: "team",
      label: "Equipe/Função",
      type: "text",
      placeholder: "Filtrar por equipe...",
    },
  ];

  return {
    movieId,
    columns,
    filterConfig,
    tableActions,
    tableData,
    isLoading,
    error,
    contractStats
  }
}